"use client";

import React, { JSX, useRef, useTransition } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUnlock } from "@fortawesome/pro-solid-svg-icons";
import { Badge } from "~/components/ui/badge";
import { useShopConfig } from "~/hooks/useShopConfig";
import { useTranslations } from "~/hooks/useTranslations";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { UNLOCK_CODE_COOKIE } from "~/lib/definitions";
import cookie from "cookiejs";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { Button } from "~/components/ui/button";
import { X } from "lucide-react";
import { useRouter } from "next/navigation";

interface IUnlockBadge {
  showText?: boolean;
  type: "section" | "price" | "global";
}

export function UnlockBadge({
  showText,
  type,
}: Readonly<IUnlockBadge>): JSX.Element {
  const router = useRouter();
  const t = useTranslations();
  const { shopConfig } = useShopConfig();
  const unlockCode = cookie.get(UNLOCK_CODE_COOKIE);
  const [isPending, startTransition] = useTransition();
  const backgroundColor =
    shopConfig.commonConfiguration?.sloganTextColor ?? undefined;

  const onRemoveUnlockCode = () => {
    startTransition(() => {
      cookie.remove(UNLOCK_CODE_COOKIE);

      router.refresh();
    });
  };

  return type === "global" ? (
    <div
      aria-label="code unlocked"
      className="group relative flex cursor-pointer flex-row flex-nowrap items-center justify-center gap-1 rounded-2xl border border-solid border-green-500 bg-green-100 px-2 py-1 text-sm font-bold text-green-500"
    >
      <div className="flex items-center gap-2">
        {isPending ? (
          <LoadingSpinner width={8} height={8} />
        ) : (
          <FontAwesomeIcon icon={faCheck} />
        )}
        <div>{unlockCode}</div>
      </div>
      {unlockCode && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onRemoveUnlockCode();
          }}
          variant="ghost"
          className="absolute right-0 aspect-square h-[27px] w-[27px] rounded-full bg-green-100 p-0 text-green-500 opacity-0 outline outline-green-500 hover:bg-green-100 group-hover:opacity-100"
        >
          <span>
            <X />
          </span>
        </Button>
      )}
    </div>
  ) : (
    <Badge
      variant="outline"
      style={{ backgroundColor }}
      className="gap-2 [&_svg]:!rotate-0"
    >
      <FontAwesomeIcon icon={faUnlock} />
      {showText && <div>{t.unlock_code.title}</div>}
    </Badge>
  );
}
